import React, { ReactElement, useContext } from 'react';
import {
  ConsumptionAndEmissionData,
  DisplayData,
  DisplayItem,
  FuelTypes,
  transformToDisplay,
} from '@oneaudi/avp-hck-dga-customerui-commons';
import i18next, { t } from 'i18next';
import styled from 'styled-components';
import { Market } from '@oneaudi/avp-models';
import { screen } from '../../common/breakpoints';
import { HorizontalSpacing, Spacing, TypoScale, VerticalSpacing } from '../../common/spacings';
import { GiveawayContext } from '../../services/GiveawayContextService';

const StyledConsumptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;

  @media screen and ${screen.s} {
    align-items: initial;
    padding-left: ${HorizontalSpacing.S};
    padding-right: ${HorizontalSpacing.S};
    padding-top: ${VerticalSpacing.S};
    padding-bottom: ${VerticalSpacing.S};
    font-size: ${TypoScale.S};
  }

  @media screen and ${screen.m} {
    padding-left: ${HorizontalSpacing.M};
    padding-right: ${HorizontalSpacing.M};
    padding-top: ${VerticalSpacing.M};
    padding-bottom: ${VerticalSpacing.M};
    flex-direction: row;
    font-size: ${TypoScale.S};
  }

  @media screen and ${screen.l} {
    align-items: initial;
    padding-left: ${HorizontalSpacing.L};
    padding-right: ${HorizontalSpacing.L};
    padding-top: ${VerticalSpacing.L};
    padding-bottom: ${VerticalSpacing.L};
    font-size: ${TypoScale.M};
  }

  @media screen and ${screen.xl} {
    align-items: initial;
    padding-left: ${HorizontalSpacing.XL};
    padding-right: ${HorizontalSpacing.XL};
    padding-top: ${VerticalSpacing.XL};
    padding-bottom: ${VerticalSpacing.XL};
    font-size: ${TypoScale.L};
  }
`;

const StyledText = styled.div`
  position: relative;

  @media ${screen.xl} {
    display: flex;
    gap: ${Spacing.S};
  }
`;

const StyledConsumptionText = styled.div`
  position: relative;
`;

const StyledEECHeading = styled.h5`
  display: flex;
  flex-wrap: wrap;
  white-space: nowrap;

  span:first-of-type {
    font-weight: bold;
    padding-right: ${Spacing.S};
  }

  span:last-of-type {
    font-weight: normal;
  }
`;

const StyledStatusHeading = styled.h5`
  padding-top: ${VerticalSpacing.XL};

  @media screen and ${screen.m} {
    padding-top: 0;
    margin-left: auto;
  }

  span {
    font-weight: normal;
  }
`;

const partitioned = (displayItems: Array<DisplayItem>): Array<Array<DisplayItem>> => {
  const displayDataRows = [];
  for (let i = 0; i < displayItems.length; i += 2) {
    displayDataRows.push(displayItems.slice(i, i + 2));
  }
  return displayDataRows;
};

const locale = (translationKey: string) => {
  if (!translationKey) {
    return '';
  }
  const fullKey = `body.details.consumption.${translationKey}`;
  const translation = t(fullKey);
  return translation === fullKey ? translationKey : translation;
};

const getCategoryTranslationKey = (category: string, fuel: string) => {
  if (category === 'consumption') {
    if (fuel === 'ELECTRICAL') {
      return 'type.electrical';
    }
    return 'type.fuel';
  }
  return category;
};
const renderPHEV = (consumption: ConsumptionAndEmissionData): ReactElement => {
  const iceConsumption = consumption!.fuels[FuelTypes.PETROL];
  const electricConsumption = consumption!.fuels[FuelTypes.ELECTRICAL];
  const { consumptionEmptyBattery } = iceConsumption;

  const energyConsumption = t('body.details.pkwEnvkv.phevEnergyConsumption', {
    phevConsumption: electricConsumption.consumption?.wltp?.subcycles.combined,
    phevConsumptionUnit: electricConsumption.consumption?.wltp?.unit,
    iceConsumption: iceConsumption.consumption?.wltp?.subcycles.combined,
    iceConsumptionUnit: iceConsumption.consumption?.wltp?.unit,
  });

  const energyConsumptionWithoutBattery = t('body.details.pkwEnvkv.phevConsumptionEmptyBattery', {
    phevEmptyBatteryConsumption: consumptionEmptyBattery?.wltp?.subcycles.combined,
    phevEmptyBatteryConsumptionUnit: consumptionEmptyBattery?.wltp?.unit,
  });

  const co2EmissionsCombined = t('body.details.pkwEnvkv.phevCo2Emission', {
    phevEmissionCombined: iceConsumption.emission?.wltp?.subcycles.combined,
    phevEmissionUnit: iceConsumption.emission?.wltp?.unit,
  });

  const co2ClassCombined = t('body.details.pkwEnvkv.co2ClassWeighted', {
    co2Class: consumption.co2Class,
  });

  const co2ClassWithoutBattery = t('body.details.pkwEnvkv.co2ClassNoBattery', {
    co2Class: consumption.co2Class,
  });

  return (
    <StyledConsumptionText
      key="phev"
      dangerouslySetInnerHTML={{
        __html: `${energyConsumption} ${energyConsumptionWithoutBattery} <br/>${co2EmissionsCombined} ${co2ClassCombined} ${co2ClassWithoutBattery}`,
      }}
    />
  );
};

const renderICE = (consumption: ConsumptionAndEmissionData): ReactElement => {
  const primaryFuel = Object.keys(consumption.fuels)[0] as FuelTypes;
  const iceConsumption = consumption.fuels[primaryFuel];

  const energyConsumption = t('body.details.pkwEnvkv.iceEnergyConsumption', {
    iceConsumption: iceConsumption.consumption?.wltp?.subcycles.combined,
    iceConsumptionUnit: iceConsumption.consumption?.wltp?.unit,
  });

  const co2EmissionsCombined = t('body.details.pkwEnvkv.iceCo2Emission', {
    iceEmissionCombined: iceConsumption.emission?.wltp?.subcycles.combined,
    iceEmissionUnit: iceConsumption.emission?.wltp?.unit,
  });

  const co2Class = t('body.details.pkwEnvkv.co2Class', {
    co2Class: consumption.co2Class,
  });

  return (
    <StyledConsumptionText
      key="ice"
      dangerouslySetInnerHTML={{
        __html: `${energyConsumption} ${co2EmissionsCombined} ${co2Class}`,
      }}
    />
  );
};

const renderBEV = (consumption: ConsumptionAndEmissionData): ReactElement => {
  const electricalConsumption = consumption.fuels[FuelTypes.ELECTRICAL];

  const energyConsumption = t('body.details.pkwEnvkv.bevEnergyConsumption', {
    bevConsumption: electricalConsumption.consumption?.wltp?.subcycles.combined,
    bevConsumptionUnit: electricalConsumption.consumption?.wltp?.unit,
  });

  const co2EmissionsCombined = t('body.details.pkwEnvkv.bevCo2Emission', {
    bevEmissionCombined: electricalConsumption.emission?.wltp?.subcycles.combined,
    bevEmissionUnit: electricalConsumption.emission?.wltp?.unit,
  });

  const co2Class = t('body.details.pkwEnvkv.co2Class', {
    co2Class: consumption.co2Class,
  });

  return (
    <StyledConsumptionText
      key="phev"
      dangerouslySetInnerHTML={{
        __html: `${energyConsumption} ${co2EmissionsCombined} ${co2Class}`,
      }}
    />
  );
};

const renderConsumptionAndEmissionData = (
  consumptionAndEmissionData: ConsumptionAndEmissionData,
) => {
  const fuels = Object.keys(consumptionAndEmissionData.fuels);

  if (fuels.includes(FuelTypes.ELECTRICAL) && fuels.includes(FuelTypes.PETROL)) {
    return renderPHEV(consumptionAndEmissionData);
  }
  if (fuels.includes(FuelTypes.ELECTRICAL)) {
    return renderBEV(consumptionAndEmissionData);
  }
  if (
    fuels.includes(FuelTypes.PETROL) ||
    fuels.includes(FuelTypes.DIESEL) ||
    fuels.includes(FuelTypes.CNG)
  ) {
    return renderICE(consumptionAndEmissionData);
  }
  throw new Error('Could not render consumption and emission data!');
};

const getTextElements = (item?: DisplayItem) => {
  if (!item) {
    return <></>;
  }
  return (
    <h5 style={{ fontWeight: 'bold' }}>
      {`${locale(item.fuel)} ${locale(getCategoryTranslationKey(item.category, item.fuel))} `}
      <span style={{ fontWeight: 'normal' }}>
        {`${locale('combined')}`}
        {`: ${item.value.toLocaleString()} ${item.unit} ${locale(item.cycle)}`}
      </span>
    </h5>
  );
};

export const Consumption = (): ReactElement => {
  const giveawayResponse = useContext(GiveawayContext);
  const { createdAt, consumptionAndEmissionData, market } = giveawayResponse;

  const requiresPkwEnVKV = (): boolean => {
    const cutOffDate = new Date('2024-05-01');
    const createdAtDate = new Date(createdAt);

    return market === Market.DEU && createdAtDate > cutOffDate;
  };

  const displayData: DisplayData = giveawayResponse.consumptionAndEmissionData
    ? transformToDisplay(giveawayResponse.consumptionAndEmissionData)
    : { items: [] };

  if (displayData.items.length === 0) {
    return <></>;
  }

  const creationDate = new Date(giveawayResponse.createdAt).toLocaleDateString(
    i18next.language.replace('_', '-'),
  );

  return (
    <StyledConsumptionContainer>
      <div data-testid="consumption-emission-section">
        {requiresPkwEnVKV()
          ? renderConsumptionAndEmissionData(consumptionAndEmissionData!)
          : partitioned(displayData.items).map((rowItems: DisplayItem[]) => {
              return (
                <StyledText key={`${rowItems[0].fuel}-${rowItems[0].cycle}`}>
                  {getTextElements(rowItems[0])}
                  {getTextElements(rowItems[1])}
                </StyledText>
              );
            })}
        {displayData.eec && (
          <StyledEECHeading style={{ display: 'flex', gap: '3px' }} data-testid="eec-section">
            <span>{locale('EEC')}:</span>
            <span>{displayData.eec}</span>
          </StyledEECHeading>
        )}
      </div>
      <div>
        <StyledStatusHeading>
          <span data-testid="creation-date">{`${locale('valuesFrom')} ${creationDate}`}</span>
        </StyledStatusHeading>
      </div>
    </StyledConsumptionContainer>
  );
};
